import { graphql } from 'gatsby'
import React, { useMemo, VFC } from 'react'
import styled from 'styled-components'

import { CartFragment } from '../../../graphql/magento'
import useSiteSettings from '../../../hooks/useSiteSettings'
import { useMagentoUserGroup } from '../../../lib/m2/user-groups'
import { formatAsCurrency } from '../../../utils/priceTools'

interface CartUpsellFreeShippingProps {
  cartPriceTotal: number | null | undefined
  shippingAddress: NonNullable<CartFragment['shipping_addresses']>[number] | undefined
}

const Banner = styled.div`
  display: flex;
  width: 100%;
  box-sizing: border-box;
  padding: 0 15px;
  height: 55px;
  background: ${({ theme }) => theme.colors.coolGray.cool100};
  border: 1px solid ${({ theme }) => theme.colors.coolGray.cool250};
  border-top: none;
  align-items: center;
`

const Message = styled.p`
  margin: 0;
`

export const CartUpsellFreeShipping: VFC<CartUpsellFreeShippingProps> = ({
  cartPriceTotal,
  shippingAddress,
}) => {
  const { freeShippingThresholds: sanityThresholds } = useSiteSettings()
  const userGroup = useMagentoUserGroup()

  const { country, region } = shippingAddress || {}

  const isNonContiguousUSShippingJurisdiction = useMemo(() => {
    return (
      country?.code === 'GU' ||
      country?.code === 'VI' ||
      (country?.code === 'US' && ['AK', 'HI', 'PR', 'VI'].includes(region?.code || ''))
    )
  }, [country, region])

  const threshold = useMemo(() => {
    if (userGroup === 'Wholesale') {
      return (
        (isNonContiguousUSShippingJurisdiction
          ? sanityThresholds?.nonContiguousProfessional
          : sanityThresholds?.professional) ?? null
      )
    }
    if (userGroup === 'Retail') {
      return (
        (isNonContiguousUSShippingJurisdiction
          ? sanityThresholds?.nonContiguousPatient
          : sanityThresholds?.patient) ?? null
      )
    }
    return null
  }, [isNonContiguousUSShippingJurisdiction, sanityThresholds, userGroup])

  const remainingToFreeShipping = useMemo(() => {
    if (threshold == null || cartPriceTotal == null) {
      return null
    }
    return Math.round(Math.max(threshold - cartPriceTotal, 0) * 100) / 100
  }, [threshold, cartPriceTotal])

  if (remainingToFreeShipping == null) {
    return null
  }

  return (
    <Banner>
      <Message>
        {remainingToFreeShipping === 0
          ? 'Free shipping is now available!'
          : `You are ${formatAsCurrency({
              amount: remainingToFreeShipping,
            })} away from receiving Free Shipping!`}
      </Message>
    </Banner>
  )
}

export const freeShippingThresholds = graphql`
  fragment freeShippingThresholds on SanitySiteSettings {
    freeShippingThresholds {
      nonContiguousPatient
      nonContiguousProfessional
      patient
      professional
    }
  }
`
